/**
 * Quarterly + Annual + CFO Statements Domain
 * Stores per-period financial summaries for reporting UI.
 */

import { useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  type AnnualReport,
  type FinancialStatements,
  type MessageType,
  type QuarterlyReport,
  type TTMMetrics,
} from '../constants/op-codes';
import { EM_DASH, formatChange, formatMargin, formatShare, normalizeSignedValue } from '../utils/format';
import { getCurrentPlayer, getCurrentTeam } from './current-team';

// The report messages as the server sends them (reports.go), type and all: the
// router's guards return these (constants/op-codes.ts), so each is said once.

export interface QuarterlyReportMessage {
  type: typeof MessageType.MessageTypeQuarterlyReport;
  quarter_id: number;
  reports: { [team_id: number]: QuarterlyReport };
}

export interface AnnualReportMessage {
  type: typeof MessageType.MessageTypeAnnualReport;
  year: number;
  reports: { [team_id: number]: AnnualReport };
  /** Set on the history a join is sent (reports.go annualHistoryEntries): a year that closed before, not one closing now. */
  replay?: boolean;
}

export interface CFOStatementsMessage {
  type: typeof MessageType.MessageTypeCFOStatements;
  quarter_id: number;
  statements: { [team_id: number]: FinancialStatements };
}

export interface TTMMetricsMessage {
  type: typeof MessageType.MessageTypeTTMMetrics;
  quarter_id: number;
  metrics: { [team_id: number]: TTMMetrics };
}

/**
 * Each report map is keyed by quarter (or year), then by team. At a quarter's
 * close the server sends every team's report in a message of its own
 * (match-handler.go, phase 4), and teams that started together close their
 * quarters on the same tick — so a message adds its teams to the quarter's map
 * and never replaces it, or the last team to be sent would wipe out the rest.
 */
interface FinancialReportsState {
  quarterlyReports: { [quarter_id: number]: { [team_id: number]: QuarterlyReport } };
  annualReports: { [year: number]: { [team_id: number]: AnnualReport } };
  quarterlyStatements: { [quarter_id: number]: { [team_id: number]: FinancialStatements } };
  ttmMetrics: { [quarter_id: number]: { [team_id: number]: TTMMetrics } };

  /** Year-end summary to display in the modal (null if none pending) */
  yearEndSummary: AnnualReport | null;

  addQuarterlyReports: (quarterId: number, reports: { [team_id: number]: QuarterlyReport }) => void;
  addAnnualReports: (year: number, reports: { [team_id: number]: AnnualReport }) => void;
  addQuarterlyStatements: (quarterId: number, statements: { [team_id: number]: FinancialStatements }) => void;
  addTTMMetrics: (quarterId: number, metrics: { [team_id: number]: TTMMetrics }) => void;
  dismissYearEndSummary: () => void;
  reset: () => void;
}

const initialState = {
  quarterlyReports: {},
  annualReports: {},
  quarterlyStatements: {},
  ttmMetrics: {},
  yearEndSummary: null as AnnualReport | null,
};

export const useFinancialReportsStore = create<FinancialReportsState>()(
  devtools(
    (set) => ({
      ...initialState,

      addQuarterlyReports: (quarterId, reports) => {
        set(
          (state) => ({
            quarterlyReports: {
              ...state.quarterlyReports,
              [quarterId]: { ...state.quarterlyReports[quarterId], ...reports },
            },
          }),
          false,
          'addQuarterlyReports'
        );
      },

      addAnnualReports: (year, reports) => {
        set(
          (state) => ({
            annualReports: {
              ...state.annualReports,
              [year]: { ...state.annualReports[year], ...reports },
            },
          }),
          false,
          'addAnnualReports'
        );
      },

      addQuarterlyStatements: (quarterId, statements) => {
        set(
          (state) => ({
            quarterlyStatements: {
              ...state.quarterlyStatements,
              [quarterId]: { ...state.quarterlyStatements[quarterId], ...statements },
            },
          }),
          false,
          'addQuarterlyStatements'
        );
      },

      addTTMMetrics: (quarterId, metrics) => {
        set(
          (state) => ({
            ttmMetrics: {
              ...state.ttmMetrics,
              [quarterId]: { ...state.ttmMetrics[quarterId], ...metrics },
            },
          }),
          false,
          'addTTMMetrics'
        );
      },

      dismissYearEndSummary: () => {
        set({ yearEndSummary: null }, false, 'dismissYearEndSummary');
      },

      reset: () => {
        set(initialState, false, 'reset');
      },
    }),
    { name: 'financial-reports-store' }
  )
);

// ==================== READERS ====================

/** A team's entry in a record kept by quarter, with the quarter it is for. */
interface QuarterEntry<T> {
  quarter: number;
  value: T;
}

/**
 * A team's newest entry in a record kept by quarter (or year), then by team.
 * Each team closes quarters on its own count, so one team's newest can carry
 * an older number than another's. Null for no team, or before its first.
 */
export function latestForTeam<T>(records: Record<number, Record<number, T>>, teamId: number | undefined): QuarterEntry<T> | null {
  if (teamId === undefined) return null;
  let latest: QuarterEntry<T> | null = null;
  for (const [key, teams] of Object.entries(records)) {
    const quarter = Number(key);
    const value = teams?.[teamId];
    if (value && (latest === null || quarter > latest.quarter)) latest = { quarter, value };
  }
  return latest;
}

/** A team's last quarterly report, with its quarter: the operating figures. */
export function useLatestReport(teamId: number | undefined): QuarterEntry<QuarterlyReport> | null {
  const reports = useFinancialReportsStore((s) => s.quarterlyReports);
  return useMemo(() => latestForTeam(reports, teamId), [reports, teamId]);
}

/** A team's last statements, with their quarter: income, balance sheet and cash flow. */
export function useLatestStatement(teamId: number | undefined): QuarterEntry<FinancialStatements> | null {
  const statements = useFinancialReportsStore((s) => s.quarterlyStatements);
  return useMemo(() => latestForTeam(statements, teamId), [statements, teamId]);
}

/** A team's trailing-twelve-month returns as its last close left them. */
export function useLatestTTM(teamId: number | undefined): QuarterEntry<TTMMetrics> | null {
  const metrics = useFinancialReportsStore((s) => s.ttmMetrics);
  return useMemo(() => latestForTeam(metrics, teamId), [metrics, teamId]);
}

/** Which side of zero a result is on, as it prints: a residue that rounds to nothing is even. */
type ResultSide = 'profit' | 'loss' | 'even';

/**
 * One closed year of a team's annual report, as the year-end board reads it.
 * Revenue is the report's own, which the server already counts cargo into —
 * adding `cargo_revenue` again would count it twice — and the operating
 * expenses are its spending, so the profit is revenue less those expenses, as
 * the server works it out.
 */
export interface YearFigures {
  year: number;
  revenue: number;
  /** Operating expenses: the year's spending. */
  expenses: number;
  /** Operating profit: revenue less operating expenses. */
  profit: number;
  /** Which side of zero the profit is on, as it prints: the one reading of a loss, for every word the board says of it. */
  side: ResultSide;
  /** The profit as a share of revenue; null in a year with no revenue. */
  margin: number | null;
  /** The share of the seat-kilometres flown that passengers filled; null in a year with no flights. */
  loadFactor: number | null;
}

export function yearFigures(report: AnnualReport): YearFigures {
  const printed = normalizeSignedValue(report.profit) ?? 0;
  return {
    year: report.year,
    revenue: report.revenue,
    expenses: report.spending,
    profit: report.profit,
    side: printed < 0 ? 'loss' : printed > 0 ? 'profit' : 'even',
    margin: report.revenue > 0 ? report.profit / report.revenue : null,
    loadFactor: report.available_seat_km > 0 ? report.passenger_km / report.available_seat_km : null,
  };
}

/**
 * A team's first year is year 1, by its number: the one reading of "no year
 * before" for every word the board says. A later year whose year before the
 * client does not hold is still a later year, never a first.
 */
function isFirstYear(figures: YearFigures): boolean {
  return figures.year === 1;
}

/** Words on the year-end board: their key, and the figures they are filled with. */
export interface YearWords {
  key: string;
  values: Record<string, string>;
}

/**
 * How the year went, in one sentence: profitable or at a loss, and how full
 * the seats flew — or that no flight flew. A team's first year says so. Each
 * case is a whole sentence of its own, so a language builds it its own way.
 */
export function yearMeaning(figures: YearFigures): YearWords {
  const first = isFirstYear(figures);
  if (figures.loadFactor === null) {
    return { key: first ? 'yearEnd.meaning.firstNoFlights' : 'yearEnd.meaning.noFlights', values: {} };
  }
  const values = { share: formatShare(figures.loadFactor) };
  if (figures.side === 'loss') return { key: first ? 'yearEnd.meaning.firstLoss' : 'yearEnd.meaning.loss', values };
  return { key: first ? 'yearEnd.meaning.firstProfit' : 'yearEnd.meaning.profit', values };
}

/**
 * The name over the year's result, whose flaps carry no sign: an operating
 * profit with its margin, or an operating loss with its share of revenue —
 * "Operating loss · 7.3% of revenue" — and either alone in a year with no
 * revenue. A year that broke even is named a profit, of nothing.
 */
export function yearResultLabel(figures: YearFigures): YearWords {
  const loss = figures.side === 'loss';
  if (figures.margin === null) {
    return { key: loss ? 'yearEnd.operatingLossNoRevenue' : 'yearEnd.operatingProfitNoRevenue', values: {} };
  }
  return loss
    ? { key: 'yearEnd.operatingLoss', values: { share: formatMargin(-figures.margin) } }
    : { key: 'yearEnd.operatingProfit', values: { margin: formatMargin(figures.margin) } };
}

/**
 * A figure's change on the year before's, as a fraction of it — 0.124 for
 * 12.4% up. Null with no year before, or none of the figure in it to change
 * from.
 */
function changeOn(before: number | undefined, now: number): number | null {
  const base = normalizeSignedValue(before);
  return base === undefined || base === 0 ? null : (now - base) / Math.abs(base);
}

/** What the year before was, said under a result that crossed zero since: its change would be on another figure. */
const TURNED: Record<Exclude<ResultSide, 'even'>, string> = {
  profit: 'yearEnd.afterProfit',
  loss: 'yearEnd.afterLoss',
};

/** What the year-end board says under each of the year's three headline figures. */
interface YearChangeWords {
  result: YearWords;
  revenue: YearWords;
  expenses: YearWords;
}

/**
 * What the year-end board says under each headline figure, so every year
 * holds the same lines. A first year has no year before, and says so under
 * each ("First year"). A later year gives each figure's change on the year
 * before ("+12.4% on year 1"), the result named as the board names it — an
 * operating profit on the profit before it, an operating loss on the loss
 * before it, so a loss that halves is down by half. A figure the year before
 * had none of, or a year before the client does not hold, has a dash for its
 * change ("— on year 2"); and a result that crossed zero says what the year
 * before was ("After a profit in year 2"), its change being on another figure.
 */
export function yearChangeWords(year: YearFigures, years: readonly YearFigures[]): YearChangeWords {
  if (isFirstYear(year)) {
    const first = { key: 'yearEnd.firstYear', values: {} };
    return { result: first, revenue: first, expenses: first };
  }
  const previous = String(year.year - 1);
  const before = years.find((other) => other.year === year.year - 1);
  const change = (fraction: number | null): YearWords => ({
    key: 'yearEnd.change',
    values: { change: fraction === null ? EM_DASH : formatChange(fraction), year: previous },
  });
  // The side the board names the year by: a year that broke even is said as a profit, of nothing.
  const named = year.side === 'loss' ? 'loss' : 'profit';
  const turned = before !== undefined && before.side !== 'even' && before.side !== named ? before.side : null;
  return {
    result: turned
      ? { key: TURNED[turned], values: { year: previous } }
      : change(before === undefined ? null : changeOn(Math.abs(before.profit), Math.abs(year.profit))),
    revenue: change(changeOn(before?.revenue, year.revenue)),
    expenses: change(changeOn(before?.expenses, year.expenses)),
  };
}

/**
 * A team's closed years from its first through `throughYear`, oldest first:
 * the years the year-end board charts. Each team closes its years on its own
 * clock, so they are the team's own; a year the client holds no report for
 * is left out rather than drawn as nothing.
 */
export function teamYears(
  annualReports: Record<number, Record<number, AnnualReport>>,
  teamId: number,
  throughYear: number,
): YearFigures[] {
  const years: YearFigures[] = [];
  for (let year = 1; year <= throughYear; year++) {
    const report = annualReports[year]?.[teamId];
    if (report) years.push(yearFigures(report));
  }
  return years;
}

// ==================== HANDLERS ====================

export function handleQuarterlyReport(data: QuarterlyReportMessage) {
  console.log('[QuarterlyReports] Received quarterly report:', data);
  useFinancialReportsStore.getState().addQuarterlyReports(data.quarter_id, data.reports);
}

export function handleAnnualReport(data: AnnualReportMessage) {
  console.log('[QuarterlyReports] Received annual report:', data);
  useFinancialReportsStore.getState().addAnnualReports(data.year, data.reports);

  // The year-end board is for a year closing now. A replay, the history every
  // join is sent, is only filed: a fresh page holds no report to tell it by,
  // and would open last year's board again.
  if (data.replay) return;

  // The server sends each team's report on its own as its year closes, so the
  // board opens on the message that carries ours.
  const teamId = getCurrentTeam()?.team_id ?? getCurrentPlayer()?.team_id;
  if (teamId && data.reports[teamId]) {
    useFinancialReportsStore.setState(
      { yearEndSummary: data.reports[teamId] },
      false,
      'setYearEndSummary'
    );
  }
}

export function handleCFOStatements(data: CFOStatementsMessage) {
  console.log('[QuarterlyReports] Received CFO statements:', data);
  useFinancialReportsStore.getState().addQuarterlyStatements(data.quarter_id, data.statements);
}

export function handleTTMMetrics(data: TTMMetricsMessage) {
  console.log('[QuarterlyReports] Received TTM metrics:', data);
  useFinancialReportsStore.getState().addTTMMetrics(data.quarter_id, data.metrics);
}

// ==================== THE PLAYER'S ACTS ====================

/** The player closes the year-end board: its Continue, or the full report it opens on its way out. */
export function dismissYearEndSummary(): void {
  useFinancialReportsStore.getState().dismissYearEndSummary();
}
